/**
 * Every date on the client's file in one list: lapsed items pinned first (they
 * never age out), then upcoming expirations and filing deadlines, then
 * priority dates. Dependents' dates carry their name.
 */
import dayjs from "dayjs";
import { CalendarDays } from "lucide-react";
import { useState, type FC } from "react";
import { useNavigate } from "react-router-dom";

import { cn } from "@/lib/utils";

import type { KeyDateRow } from "../../types";
import { CardRow, DashboardCard, ShowMoreFooter } from "./DashboardCard";

const COLLAPSED = 6;
const SOON_DAYS = 90;
const ENDED_KINDS = new Set([
  "immigration_status_expiration",
  "i94_expiration"
]);

type Tone = "red" | "amber" | "neutral";

const TONE_CLASS: Record<Tone, string> = {
  red: "bg-destructive/10 text-destructive",
  amber: "bg-warn-soft text-warn",
  neutral: "bg-muted text-muted-foreground"
};

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

export const keyDateChip = (
  row: KeyDateRow
): { text: string; tone: Tone } | null => {
  if (row.category === "milestone") return null;
  if (row.expired) {
    const ago = plural(-row.days_left, "day");
    if (row.kind === "visa_expiration")
      return { text: "Expired · matters only for travel", tone: "neutral" };
    const verb = ENDED_KINDS.has(row.kind) ? "Ended" : "Expired";
    return { text: `${verb} ${ago} ago`, tone: "red" };
  }
  if (row.days_left === 0) return { text: "Today", tone: "red" };
  if (row.days_left <= SOON_DAYS)
    return { text: `In ${plural(row.days_left, "day")}`, tone: "amber" };
  return null;
};

/** A deadline row's second line: the notice response it is, or a filing. */
export const keyDateDeadlineKind = (row: KeyDateRow): string =>
  row.kind === "notice_response" ? "Response deadline" : "Filing deadline";

type Props = {
  items: KeyDateRow[] | undefined;
  isLoading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  onViewAll?: () => void;
  span?: "full";
};

export const KeyDatesCard: FC<Props> = ({
  items,
  isLoading,
  error,
  onRetry,
  onViewAll,
  span
}) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const rows = items ?? [];
  const pinned = rows.filter((r) => r.expired).length;
  const limit = Math.max(COLLAPSED, pinned);
  const shown = expanded ? rows : rows.slice(0, limit);
  return (
    <DashboardCard
      icon={CalendarDays}
      title="Key Dates"
      count={rows.length}
      onViewAll={onViewAll}
      isLoading={isLoading && !items}
      error={error}
      onRetry={onRetry}
      isEmpty={rows.length === 0}
      emptyTitle="No Dates on File"
      emptyDescription="Status, I-94, passport, EAD and filing dates show up here once they're on the profile."
      span={span}
      testId="key-dates-card"
      footer={
        <ShowMoreFooter
          hidden={rows.length - shown.length}
          expanded={expanded}
          onToggle={() => setExpanded((v) => !v)}
          label="Dates"
        />
      }
    >
      {shown.map((row) => {
        const chip = keyDateChip(row);
        return (
          <CardRow
            key={`${row.kind}:${row.dependent_id ?? row.individual_id ?? ""}:${
              row.due
            }`}
            onClick={() => navigate(row.path)}
            primary={
              <>
                {row.label}
                {row.subject ? (
                  <span className="text-muted-foreground">
                    {" "}
                    · {row.subject}
                  </span>
                ) : null}
              </>
            }
            secondary={
              row.category === "deadline"
                ? keyDateDeadlineKind(row)
                : row.subject
                ? "Dependent"
                : undefined
            }
            trailing={
              <>
                <span className="text-xs tabular-nums text-muted-foreground">
                  {dayjs(row.due.slice(0, 10)).format("MMM D, YYYY")}
                </span>
                {chip ? (
                  <span
                    className={cn(
                      "rounded px-1.5 py-0.5 text-xs font-medium",
                      TONE_CLASS[chip.tone]
                    )}
                  >
                    {chip.text}
                  </span>
                ) : null}
              </>
            }
            testId={row.expired ? "key-date-lapsed" : "key-date-row"}
          />
        );
      })}
    </DashboardCard>
  );
};
